import { convertAnyDateToNumber } from "../apisV2";
import { reduxApiSlice } from "./index";
import type { PutApiSettingsUpdateRequestBody } from "@superblocksteam/schemas";
import type {
  PutApiSettingsUpdateResponseBody,
  ResponseWrapper,
  ScheduleState,
} from "@superblocksteam/shared";
import type { Metadata } from "@superblocksteam/types";
import type { ApiDtoWithPb } from "store/slices/apisV2/slice";
import type { AppState } from "store/types";

type PatchApiMetadataResponseBody = {
  updated: string;
  metadata: Metadata;
};

const extendedApi = reduxApiSlice.injectEndpoints({
  endpoints: (builder) => ({
    listWorkflowsAndScheduledJobs: builder.query<ApiDtoWithPb[], void>({
      query: () => `v3/apis`,
      transformResponse: (response: { data: ApiDtoWithPb[] }) => {
        return response.data;
      },

      providesTags: (result = []) => [
        { type: "WorkflowsAndScheduledJobs" as const },
        ...result.map(({ id }) => ({
          type: "WorkflowsAndScheduledJobs" as const,
          id,
        })),
      ],
    }),

    updateWorkflowOrScheduledJob: builder.mutation<
      PutApiSettingsUpdateResponseBody,
      {
        id: string;
        updates: Partial<ApiDtoWithPb>;
      }
    >({
      queryFn: async ({ id, updates }, { getState }, _, baseQuery) => {
        try {
          // always use the latest state to avoid stale references
          const { data } =
            extendedApi.endpoints.listWorkflowsAndScheduledJobs.select()(
              // https://redux-toolkit.js.org/rtk-query/usage-with-typescript#typing-dispatch-and-getstate
              getState() as AppState,
            );

          const api = data?.find((api) => api.id === id);
          if (!api) {
            throw new Error("API not found");
          }

          const lastSuccessfulWriteForApi = convertAnyDateToNumber(api.updated);

          if (!lastSuccessfulWriteForApi) {
            throw new Error("Last successful write not found");
          }

          const body = {
            folderId: updates.folderId,
            lastSuccessfulWrite: lastSuccessfulWriteForApi,
          } satisfies PutApiSettingsUpdateRequestBody;

          const result = (await baseQuery({
            url: `v3/apis/${id}/settings`,
            method: "PUT",
            body,
          })) as {
            data: ResponseWrapper<PutApiSettingsUpdateResponseBody>;
            meta: { request: Request; response: Response };
          };

          if (!result.meta.response.ok) {
            throw new Error(result.data.responseMeta.message);
          }

          if (!result.data?.data) {
            throw new Error("API settings update failed");
          }

          return { data: result.data.data };
        } catch (error) {
          return { error };
        }
      },

      // We avoid refetching the list of workflows and jobs because it's expensive and there's no need.
      invalidatesTags: () => [],

      // Handle optimistic update instead
      async onQueryStarted({ id, updates }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          extendedApi.util.updateQueryData(
            "listWorkflowsAndScheduledJobs",
            undefined,
            (draft) => {
              const itemIndex = draft.findIndex((item) => item.id === id);
              if (itemIndex !== -1) {
                draft[itemIndex] = {
                  ...draft[itemIndex],
                  ...updates,
                  // TODO: do we need updated time here?
                };
              }
            },
          ),
        );

        try {
          const { data: apiSettingsResp } = await queryFulfilled;

          dispatch(
            extendedApi.util.updateQueryData(
              "listWorkflowsAndScheduledJobs",
              undefined,
              (draft) => {
                const itemIndex = draft.findIndex((item) => item.id === id);
                if (itemIndex !== -1) {
                  draft[itemIndex].updated = new Date(apiSettingsResp.updated);
                }
              },
            ),
          );
        } catch {
          patchResult.undo();
        }
      },
    }),

    deleteWorkflowsOrJobs: builder.mutation<void, { id: string }>({
      query: ({ id }) => ({
        url: `v3/apis/${id}`,
        method: "DELETE",
      }),
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          extendedApi.util.updateQueryData(
            "listWorkflowsAndScheduledJobs",
            undefined,
            (draft) => {
              const index = draft.findIndex((item) => item.id === id);
              if (index !== -1) {
                draft.splice(index, 1);
              }
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),

    renameWorkflowOrScheduledJob: builder.mutation<
      PatchApiMetadataResponseBody,
      { id: string; name: string; lastSuccessfulWrite: number | undefined }
    >({
      query: ({ id, name, lastSuccessfulWrite }) => ({
        url: `/v3/apis/${id}/metadata`,
        method: "PATCH",
        body: { metadata: { name }, lastSuccessfulWrite },
      }),
      transformResponse: (response: { data: PatchApiMetadataResponseBody }) => {
        return response.data;
      },
      invalidatesTags: () => [],
      async onQueryStarted({ id, name }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          extendedApi.util.updateQueryData(
            "listWorkflowsAndScheduledJobs",
            undefined,
            (draft) => {
              const itemIndex = draft.findIndex((item) => item.id === id);
              if (itemIndex !== -1) {
                draft[itemIndex].name = name;
              }
            },
          ),
        );
        try {
          const { data: apiResponse } = await queryFulfilled;
          dispatch(
            extendedApi.util.updateQueryData(
              "listWorkflowsAndScheduledJobs",
              undefined,
              (draft) => {
                const itemIndex = draft.findIndex((item) => item.id === id);
                if (itemIndex !== -1) {
                  draft[itemIndex].updated = new Date(apiResponse.updated);
                }
              },
            ),
          );
        } catch {
          patchResult.undo();
        }
      },
    }),

    updateScheduleState: builder.mutation<
      void,
      { id: string; scheduleState: ScheduleState }
    >({
      query: ({ id, scheduleState }) => ({
        url: `v3/scheduled-jobs/${id}/schedule-state`,
        method: "PUT",
        body: { scheduleState },
      }),
      invalidatesTags: () => [],
      async onQueryStarted(
        { id, scheduleState },
        { dispatch, queryFulfilled },
      ) {
        const patchResult = dispatch(
          extendedApi.util.updateQueryData(
            "listWorkflowsAndScheduledJobs",
            undefined,
            (draft) => {
              const itemIndex = draft.findIndex((item) => item.id === id);
              if (itemIndex !== -1) {
                draft[itemIndex].scheduleState = scheduleState;
              }
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
  }),
  overrideExisting: false,
});

export const {
  useListWorkflowsAndScheduledJobsQuery,
  useUpdateWorkflowOrScheduledJobMutation,
  useDeleteWorkflowsOrJobsMutation,
  useRenameWorkflowOrScheduledJobMutation,
  useUpdateScheduleStateMutation,
  util: workflowsAndScheduledJobsApiUtil,
} = extendedApi;
